import { useState } from 'react';
import { Map } from 'ol';
import InsetMap from '../components/InsetMap';
import { useTheme } from '../theme/ThemeContext';
import { getMapControlsTheme } from '../theme/themeStyles';

interface AnalysisInfo {
  type: string;
  index?: string;
  date?: string;
  value?: string;
}

interface MapStatusIndicatorsProps {
  analysisInfo: AnalysisInfo | null;
  zoomLevel: number;
  mapCoordinates: [number, number];
  selectedField: any;
  isLoadingGeoJson: boolean;
  mainMap?: Map;
}

export default function MapStatusIndicators({
  zoomLevel,
  mapCoordinates,
  selectedField,
  isLoadingGeoJson,
  mainMap,
}: MapStatusIndicatorsProps) {
  const { isDark } = useTheme();
  const mc = getMapControlsTheme(isDark);
  const [showMapInfo, setShowMapInfo] = useState(false);
  const [showInsetMap, setShowInsetMap] = useState(false);

  return (
    <>
      {isLoadingGeoJson && (
        <div className={mc.loadingShell}>
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-500" />
            <span className={mc.loadingText}>Loading field boundaries...</span>
          </div>
        </div>
      )}

      {showInsetMap && (
        <InsetMap mainMap={mainMap} className="!absolute bottom-16 left-14" />
      )}

      <div className="absolute bottom-4 left-2 z-10 flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={() => setShowInsetMap(!showInsetMap)}
          className={showInsetMap ? mc.floatingBtnActive : mc.floatingBtn}
          title={showInsetMap ? 'Hide overview map' : 'Show overview map'}
        >
          <i className="ri-map-2-line text-base" />
        </button>

        <button
          type="button"
          onClick={() => setShowMapInfo(!showMapInfo)}
          className={showMapInfo ? mc.floatingBtnActive : mc.floatingBtn}
          title={showMapInfo ? 'Hide map information' : 'Show map information'}
        >
          <i className={`ri-${showMapInfo ? 'information-line' : 'information-2-line'} text-base`} />
        </button>
      </div>

      {showMapInfo && (
        <div className={mc.infoBar}>
          <div className="flex items-center space-x-2">
            <i className={`ri-zoom-in-line ${mc.infoIcon}`} />
            <span className="font-medium">Zoom: {Math.round(zoomLevel)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <i className={`ri-map-pin-line ${mc.infoIcon}`} />
            <span className="font-medium">
              {mapCoordinates[1].toFixed(4)}, {mapCoordinates[0].toFixed(4)}
            </span>
          </div>
          {selectedField && (
            <div className="flex items-center space-x-2">
              <i className={`ri-plant-line ${mc.infoIcon}`} />
              <span className="font-medium">Field: {selectedField.farm_name}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}
